import React, { useMemo, useState } from "react";
import {
	Box,
	Button,
	Drawer,
	DrawerBody,
	DrawerCloseButton,
	DrawerContent,
	DrawerFooter,
	DrawerHeader,
	DrawerOverlay,
	Flex,
	Text,
} from "@chakra-ui/react";
import { ChakraFAIcon } from "@/components/ui/Icon";
import { useToast } from "@/components/ui/Toast";
import { transformPauseStudents } from "./utils";
import { pauseGivenStudents } from "@/api/pause";
import useStore from "@/stores/index";
import { colours } from "@/utils/themeUtils";
import { useClassroomPolicyStore } from "@/stores/v2/classroomPolicyStore";
import { StudentCheckboxSelector } from "@/components/StudentCheckboxSelector/StudentCheckboxSelector";
import { studentClaimsToMapByStudentId, useStudentClaimsQuery } from "@/query/studentClaimsQuery";
import { useTranslation } from "react-i18next";

export interface PausableStudent {
	qoriaUserId: string;
	username: string;
	fname: string;
	lname: string;
}

interface Props {
	isOpen: boolean;
	closeDrawer: () => void;
	classId: string;
	students: PausableStudent[];
	pausedStudentIds: string[];
}

export function PauseInternetDrawer(props: Props): JSX.Element {
	const { t } = useTranslation();
	const [selectedStudentIds, setSelectedStudentIds] = useState<string[]>([]);

	const [isPausing, setIsPausing] = useState(false);

	const cwStore = useStore();
	const toast = useToast();

	const classroomPolicyStore = useClassroomPolicyStore();

	function handleCloseDrawer(): void {
		props.closeDrawer();
	}

	const studentClaimsQuery = useStudentClaimsQuery({ classroomStableId: props.classId });
	const studentClaimsMap = useMemo(() => studentClaimsToMapByStudentId(studentClaimsQuery?.data ?? []), [studentClaimsQuery?.data]);

	const selectableStudents = transformPauseStudents(props.students, props.classId, studentClaimsMap);

	function handleSelectionChange(selectedStudents: string[]): void {
		setSelectedStudentIds(selectedStudents);
	}

	function handleSubmit(): void {
		setIsPausing(true);
		pauseGivenStudents(cwStore.classroom.getTeacherStableIdV1(), props.classId, selectedStudentIds)
			.then(() => {
				classroomPolicyStore.setPausedStudents(selectedStudentIds);
				handleCloseDrawer();
			})
			.catch(() => {
				toast.errorToast({
					title: t("pauseInternetDrawer.toastErrorTitle"),
					description: t("pauseInternetDrawer.toastErrorDescription"),
					isClosable: true,
				});
			})
			.finally(() => {
				setIsPausing(false);
			});
	}

	const hasSelectionChanged = selectedStudentIds.sort().join(",") !== props.pausedStudentIds.sort().join(",");

	return (
		<Drawer isOpen={props.isOpen} onClose={handleCloseDrawer} size={"md"}>
			<DrawerOverlay />
			<DrawerContent>
				<DrawerCloseButton />
				<DrawerHeader as={Box} p={7} background={"#F1F2F4"}>
					<Flex justifyContent={"space-between"}>
						<Text fontSize={"xl"} fontWeight={500} mb={3}>
							{t("pauseInternetDrawer.title")}
						</Text>
					</Flex>
					<Text color={colours.navy[800]} fontSize={"md"} fontWeight={400}>
						{t("pauseInternetDrawer.description")}
					</Text>
				</DrawerHeader>
				<DrawerBody>
					<Box mt={2}>
						<StudentCheckboxSelector
							students={selectableStudents}
							handleSelectionChange={handleSelectionChange}
							preselectedStudentValues={props.pausedStudentIds}
						/>
					</Box>
				</DrawerBody>
				<DrawerFooter p={5} background={"#F1F2F4"}>
					<Button
						float={"right"}
						leftIcon={<ChakraFAIcon icon="fa-pause" />}
						onClick={handleSubmit}
						isDisabled={selectedStudentIds.length === 0 || !hasSelectionChanged}
						isLoading={isPausing}
						data-testid="pause-drawer-button"
					>
						{t("pauseInternetDrawer.pauseButton")}
					</Button>
				</DrawerFooter>
			</DrawerContent>
		</Drawer>
	);
}
